import type { CSSProperties, ReactNode } from 'react';
import { useI18n } from '../lib/i18n';
import { positionKey } from './PositionTools';

type Depth = { bids: Array<[number, number]>; asks: Array<[number, number]> };
type Pos = {
  symbol?: string;
  side?: string;
  size?: number;
  positionAmt?: number;
  entryPrice?: number;
  unrealizedPnl?: number;
  markPrice?: number;
  leverage?: number;
};
type Order = { symbol?: string; side?: string; price?: number; origQty?: number; quantity?: number; orderId?: string; type?: string };
type Trade = {
  price?: number;
  qty?: number;
  quantity?: number;
  time?: number;
  isBuyerMaker?: boolean;
  symbol?: string;
  side?: string;
  real?: boolean;
  label?: string;
  reduceOnly?: boolean;
  source?: string;
  /** FIFO realized PnL in USDT for exit fills; null on entries. */
  realizedPnl?: number | null;
  /** Position-move % = PnL / entry notional × 100. */
  realizedPnlPct?: number | null;
};

type TapeTrade = {
  price?: number;
  qty?: number;
  quantity?: number;
  size?: number;
  time?: number;
  ts?: number;
  timestamp?: number;
  side?: string;
  isBuyerMaker?: boolean;
};

type Props = {
  tab: 'depth' | 'positions' | 'orders' | 'trades' | 'balance';
  onTab: (t: Props['tab']) => void;
  depth: Depth | null;
  positions: Pos[];
  orders: Order[];
  /** Closed / filled trades for this tab (not the public market tape). */
  trades: Trade[];
  /** Public market tape (Book tab). */
  marketTape?: TapeTrade[];
  balance?: { equity: number; available: number } | null;
  hasKey: boolean;
  lastPrice?: number | null;
  /** Read-only exchange margin summary (controls live in Order / Strategies). */
  marginLabel?: string | null;
  onRefreshDepth?: () => void;
  onRefreshTrades?: () => void;
  onRefreshTape?: () => void;
  onRefreshAccount?: () => void;
  /** Selected open position key `SYMBOL:long|short`. */
  selectedPositionKey?: string | null;
  onSelectPosition?: (key: string | null, pos: Pos | null) => void;
  /** Rendered under the positions table when a row is selected. */
  positionTools?: ReactNode;
  /** Active chart tab symbol — used for group-by-tab filter. */
  activeSymbol?: string | null;
  /** When true, only show positions / orders / trades for the open chart tab. */
  groupByActiveTab?: boolean;
  onGroupByActiveTab?: (v: boolean) => void;
  /** Jump desk tab to this symbol (create/switch workspace). */
  onJumpToSymbol?: (symbol: string) => void;
  /** Live AI Chip short tag by normalized symbol (Pulse/Snap/Ladder/Book). */
  chipBySymbol?: Record<string, string>;
  /** Left-column dock: split · chart covers panel · panel covers chart. */
  dock?: 'split' | 'chart' | 'terminal';
  onDockChange?: (v: 'split' | 'chart' | 'terminal') => void;
  /**
   * Click a DOM / ladder / tape price → fill Limit on the Order ticket.
   * `side`: book side that was clicked (ask → typically buy, bid → sell).
   */
  onBookLevelClick?: (level: { price: number; side: 'bid' | 'ask' }) => void;
};

const fmt = (n: unknown, d = 4) => {
  const x = Number(n);
  if (!Number.isFinite(x)) return '—';
  const abs = Math.abs(x);
  const digits = abs > 0 && abs < 0.01 ? Math.max(d, 6) : abs < 1 ? Math.max(d, 5) : d;
  return x.toLocaleString('en-US', { maximumFractionDigits: digits });
};

const normSym = (s: unknown) => String(s || '')
  .toUpperCase()
  .replace(/^CMT_?/i, '')
  .replace(/[:/\-_]/g, '')
  .replace(/USDTUSDT$/, 'USDT');

export default function TerminalPanel({
  tab, onTab, depth, positions, orders, trades, marketTape = [], balance, hasKey, lastPrice,
  marginLabel = null,
  onRefreshDepth, onRefreshTrades, onRefreshTape, onRefreshAccount,
  selectedPositionKey = null,
  onSelectPosition,
  positionTools = null,
  activeSymbol = null,
  groupByActiveTab = false,
  onGroupByActiveTab,
  onJumpToSymbol,
  chipBySymbol = {},
  dock = 'split',
  onDockChange,
  onBookLevelClick,
}: Props) {
  const expanded = dock === 'terminal';
  const { t, lang } = useI18n();
  const tabs: Array<Props['tab']> = ['positions', 'orders', 'trades', 'depth', 'balance'];
  const labels: Record<Props['tab'], string> = {
    positions: t.positions,
    orders: t.orders,
    trades: t.trades,
    depth: t.depth,
    balance: t.balance,
  };

  const refresh = () => {
    if (tab === 'depth') {
      onRefreshDepth?.();
      onRefreshTape?.();
    } else if (tab === 'trades') onRefreshTrades?.();
    else onRefreshAccount?.();
  };

  const maxQty = Math.max(
    1,
    ...(depth?.bids || []).map(([, q]) => q),
    ...(depth?.asks || []).map(([, q]) => q),
  );

  const activeSym = normSym(activeSymbol);
  const shownPositions = groupByActiveTab && activeSym
    ? positions.filter((p) => normSym(p.symbol) === activeSym)
    : positions;
  const shownOrders = groupByActiveTab && activeSym
    ? orders.filter((o) => normSym(o.symbol) === activeSym)
    : orders;
  const shownTrades = groupByActiveTab && activeSym
    ? trades.filter((tr) => normSym(tr.symbol) === activeSym)
    : trades;
  const showTabFilter = (tab === 'positions' || tab === 'orders' || tab === 'trades')
    && Boolean(onGroupByActiveTab);

  return (
    <div style={{
      height: '100%', minHeight: 0, display: 'grid', gridTemplateRows: 'auto minmax(0,1fr)',
      background: 'var(--panel)', borderRadius: 8, overflow: 'hidden',
    }}>
      <div style={{
        display: 'flex', gap: 4, padding: '4px 6px', borderBottom: '1px solid var(--line)',
        alignItems: 'center', flexShrink: 0, flexWrap: 'wrap', rowGap: 4,
      }}>
        <div style={{ display: 'flex', gap: 3, alignItems: 'center', flex: '1 1 auto', minWidth: 0, flexWrap: 'wrap' }}>
          {tabs.map((id) => (
            <button
              key={id}
              type="button"
              onClick={() => onTab(id)}
              style={{
                ...tabBtn,
                padding: '5px 8px',
                fontSize: 12,
                borderColor: tab === id ? 'var(--gold)' : 'var(--line)',
                color: tab === id ? 'var(--gold)' : 'var(--text)',
                whiteSpace: 'nowrap',
              }}
            >
              {labels[id]}
            </button>
          ))}
          {showTabFilter ? (
            <label
              title={t.posGroupByTabTitle}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                marginLeft: 2,
                padding: '2px 6px',
                borderRadius: 7,
                border: '1px solid rgba(201,162,39,0.22)',
                background: 'rgba(201,162,39,0.04)',
                fontSize: 11,
                color: groupByActiveTab ? 'var(--gold)' : 'var(--muted)',
                cursor: 'pointer',
                userSelect: 'none',
                whiteSpace: 'nowrap',
              }}
            >
              <input
                type="checkbox"
                checked={groupByActiveTab}
                onChange={(e) => onGroupByActiveTab?.(e.target.checked)}
              />
              {t.posGroupByTab}
            </label>
          ) : null}
        </div>
        <div style={{
          marginLeft: 'auto', display: 'flex', gap: 4, alignItems: 'center',
          fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--muted)', flexShrink: 0,
        }}>
          {marginLabel ? <span title={t.margin} style={{ maxWidth: 110, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{marginLabel}</span> : null}
          {lastPrice != null ? <span>{`last ${fmt(lastPrice, 2)}`}</span> : <span>—</span>}
          {!hasKey ? <span>{` · ${t.noKeyShort}`}</span> : null}
          {onDockChange ? (
            <>
              {expanded ? (
                <button
                  type="button"
                  onClick={() => onDockChange('split')}
                  style={{ ...tabBtn, padding: '5px 8px', fontSize: 12 }}
                  title={t.termSplitTitle}
                >
                  {t.termSplit}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => onDockChange('terminal')}
                  style={{ ...tabBtn, padding: '5px 8px', fontSize: 12 }}
                  title={t.termExpandPanelTitle}
                >
                  {t.termExpandPanel}
                </button>
              )}
              <button
                type="button"
                onClick={() => onDockChange('chart')}
                style={{ ...tabBtn, padding: '5px 8px', fontSize: 12 }}
                title={t.termExpandChartTitle}
              >
                {t.termExpandChart}
              </button>
            </>
          ) : null}
          <button type="button" onClick={refresh} style={{ ...tabBtn, padding: '5px 8px', fontSize: 12 }} title={t.refresh}>↻</button>
        </div>
      </div>

      <div style={{
        overflow: 'auto', WebkitOverflowScrolling: 'touch', padding: 8,
        fontFamily: 'var(--mono)', fontSize: 12, minHeight: 0,
      }}>
        {tab === 'depth' && (
          depth ? (() => {
            const levels = expanded ? 28 : 14;
            const asks = [...(depth.asks || [])].slice(0, levels).sort((a, b) => a[0] - b[0]); // low→high
            const bids = [...(depth.bids || [])].slice(0, levels).sort((a, b) => b[0] - a[0]); // high→low
            const asksDesc = [...asks].reverse(); // high ask on top (Tiger ladder)
            let cumAsk = 0;
            const askRows = asksDesc.map(([p, q]) => {
              cumAsk += q;
              return { p, q, cum: cumAsk };
            });
            let cumBid = 0;
            const bidRows = bids.map(([p, q]) => {
              cumBid += q;
              return { p, q, cum: cumBid };
            });
            const maxCum = Math.max(1, ...askRows.map((r) => r.cum), ...bidRows.map((r) => r.cum), maxQty);
            const maxSideQty = Math.max(
              1,
              ...asks.map(([, q]) => q),
              ...bids.map(([, q]) => q),
            );
            const mid = lastPrice
              || (asks[0] && bids[0] ? (asks[0][0] + bids[0][0]) / 2 : asks[0]?.[0] || bids[0]?.[0] || 0);
            const spread = asks[0] && bids[0] ? asks[0][0] - bids[0][0] : null;
            const armLimit = (bookSide: 'ask' | 'bid', price: number) => {
              if (!(price > 0) || !onBookLevelClick) return;
              onBookLevelClick({ price, side: bookSide });
            };
            const ladderRow = (
              side: 'ask' | 'bid',
              r: { p: number; q: number; cum: number },
            ) => (
              <div
                key={`lad-${side}${r.p}`}
                role={onBookLevelClick ? 'button' : undefined}
                tabIndex={onBookLevelClick ? 0 : undefined}
                title={t.bookClickSoon}
                onClick={() => armLimit(side, r.p)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    armLimit(side, r.p);
                  }
                }}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr 1fr',
                  position: 'relative',
                  padding: '1px 4px',
                  fontSize: 10,
                  lineHeight: 1.3,
                  cursor: onBookLevelClick ? 'pointer' : 'default',
                }}
              >
                <div style={{
                  position: 'absolute', top: 1, bottom: 1,
                  ...(side === 'ask'
                    ? { right: 0, background: 'rgba(226,91,91,0.18)' }
                    : { left: 0, background: 'rgba(61,186,122,0.18)' }),
                  width: `${Math.min(100, (r.cum / maxCum) * 100)}%`,
                  borderRadius: 2,
                }} />
                <span style={{ color: side === 'ask' ? 'var(--short)' : 'var(--long)', position: 'relative' }}>{fmt(r.p)}</span>
                <span style={{ textAlign: 'right', position: 'relative' }}>{fmt(r.q, 3)}</span>
                <span style={{ textAlign: 'right', color: 'var(--muted)', position: 'relative' }}>{fmt(r.cum, 3)}</span>
              </div>
            );
            const domCell = (side: 'ask' | 'bid', p: number, q: number) => (
              <div
                key={`dom-${side}${p}`}
                role={onBookLevelClick ? 'button' : undefined}
                tabIndex={onBookLevelClick ? 0 : undefined}
                title={t.bookClickSoon}
                onClick={() => armLimit(side, p)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    armLimit(side, p);
                  }
                }}
                style={{
                  display: 'grid',
                  gridTemplateColumns: side === 'bid' ? '1fr 1fr' : '1fr 1fr',
                  position: 'relative',
                  padding: '1px 4px',
                  fontSize: 10,
                  lineHeight: 1.3,
                  cursor: onBookLevelClick ? 'pointer' : 'default',
                }}
              >
                <div style={{
                  position: 'absolute', top: 1, bottom: 1,
                  ...(side === 'ask'
                    ? { left: 0, background: 'rgba(226,91,91,0.22)' }
                    : { right: 0, background: 'rgba(61,186,122,0.22)' }),
                  width: `${Math.min(100, (q / maxSideQty) * 100)}%`,
                  borderRadius: 2,
                }} />
                {side === 'bid' ? (
                  <>
                    <span style={{ color: 'var(--long)', position: 'relative' }}>{fmt(p)}</span>
                    <span style={{ textAlign: 'right', position: 'relative' }}>{fmt(q, 3)}</span>
                  </>
                ) : (
                  <>
                    <span style={{ position: 'relative' }}>{fmt(q, 3)}</span>
                    <span style={{ textAlign: 'right', color: 'var(--short)', position: 'relative' }}>{fmt(p)}</span>
                  </>
                )}
              </div>
            );
            const tapeRows = (marketTape || []).slice(0, expanded ? 40 : 18).map((tr, i) => {
              const px = Number(tr.price);
              const qty = Number(tr.qty ?? tr.quantity ?? tr.size ?? 0);
              const ts = Number(tr.time ?? tr.ts ?? tr.timestamp ?? 0);
              const sideRaw = String(tr.side || '').toLowerCase();
              const sell = tr.isBuyerMaker === true
                || sideRaw === 'sell' || sideRaw === 'ask' || sideRaw === 's';
              return (
                <div
                  key={`tape-${i}-${px}-${ts}`}
                  role={onBookLevelClick && px > 0 ? 'button' : undefined}
                  tabIndex={onBookLevelClick && px > 0 ? 0 : undefined}
                  title={t.bookClickSoon}
                  onClick={() => armLimit(sell ? 'ask' : 'bid', px)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      armLimit(sell ? 'ask' : 'bid', px);
                    }
                  }}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr 1fr',
                    gap: 2,
                    padding: '1px 2px',
                    fontSize: 10,
                    color: sell ? 'var(--short)' : 'var(--long)',
                    cursor: onBookLevelClick && px > 0 ? 'pointer' : 'default',
                  }}
                >
                  <span>{fmt(px)}</span>
                  <span style={{ textAlign: 'right' }}>{fmt(qty, 3)}</span>
                  <span style={{ textAlign: 'right', color: 'var(--muted)' }}>
                    {ts > 0
                      ? new Date(ts < 1e12 ? ts * 1000 : ts).toLocaleTimeString(lang === 'ru' ? 'ru-RU' : 'en-US', {
                        hour: '2-digit', minute: '2-digit', second: '2-digit',
                      })
                      : '—'}
                  </span>
                </div>
              );
            });
            const colHead: CSSProperties = {
              fontSize: 10, color: 'var(--muted)', padding: '0 4px 4px',
              borderBottom: '1px solid var(--line)', marginBottom: 4, fontWeight: 600,
            };
            return (
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1.15fr 1fr 0.85fr',
                gap: 8,
                height: '100%',
                minHeight: 0,
                alignItems: 'stretch',
              }}>
                <div style={{ minWidth: 0, overflow: 'auto', borderRight: '1px solid var(--line)', paddingRight: 6 }}>
                  <div style={colHead}>{t.bookDom} · {t.bookDensity}</div>
                  {onBookLevelClick ? (
                    <div style={{ fontSize: 9, color: 'var(--muted)', marginBottom: 4, lineHeight: 1.3 }}>
                      {t.bookClickHint}
                    </div>
                  ) : null}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
                    <div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', color: 'var(--muted)', fontSize: 9, padding: '0 4px 2px' }}>
                        <span>{t.bookPrice}</span>
                        <span style={{ textAlign: 'right' }}>{t.bookQty}</span>
                      </div>
                      {bids.map(([p, q]) => domCell('bid', p, q))}
                    </div>
                    <div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', color: 'var(--muted)', fontSize: 9, padding: '0 4px 2px' }}>
                        <span>{t.bookQty}</span>
                        <span style={{ textAlign: 'right' }}>{t.bookPrice}</span>
                      </div>
                      {asks.map(([p, q]) => domCell('ask', p, q))}
                    </div>
                  </div>
                  <div style={{
                    marginTop: 6, padding: '4px 6px', borderRadius: 6,
                    border: '1px solid rgba(223,184,58,0.35)', background: 'rgba(223,184,58,0.06)',
                    display: 'flex', justifyContent: 'space-between', fontSize: 11,
                  }}>
                    <span style={{ color: 'var(--gold)', fontWeight: 700 }}>{fmt(mid)}</span>
                    <span style={{ color: 'var(--muted)' }}>
                      {spread != null && Number.isFinite(spread) ? `spr ${fmt(spread)}` : 'mid'}
                    </span>
                  </div>
                </div>
                <div style={{ minWidth: 0, overflow: 'auto', borderRight: '1px solid var(--line)', paddingRight: 6 }}>
                  <div style={colHead}>{t.bookLadder}</div>
                  <div style={{
                    display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2,
                    padding: '0 4px 4px', color: 'var(--muted)', fontSize: 9,
                  }}>
                    <span>{t.bookPrice}</span>
                    <span style={{ textAlign: 'right' }}>{t.bookQty}</span>
                    <span style={{ textAlign: 'right' }}>{t.bookCum}</span>
                  </div>
                  {askRows.map((r) => ladderRow('ask', r))}
                  <div style={{
                    margin: '4px 2px', padding: '4px 6px', borderRadius: 6,
                    border: '1px solid var(--gold)', background: 'rgba(223,184,58,0.08)',
                    display: 'flex', justifyContent: 'space-between', fontSize: 11,
                  }}>
                    <span style={{ color: 'var(--gold)', fontWeight: 700 }}>{fmt(mid)}</span>
                    <span style={{ color: 'var(--muted)', fontSize: 10 }}>
                      {spread != null && Number.isFinite(spread) ? `spr ${fmt(spread)}` : 'mid'}
                    </span>
                  </div>
                  {bidRows.map((r) => ladderRow('bid', r))}
                </div>
                <div style={{ minWidth: 0, overflow: 'auto' }}>
                  <div style={colHead}>{t.bookTape}</div>
                  <div style={{
                    display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2,
                    padding: '0 2px 4px', color: 'var(--muted)', fontSize: 9,
                  }}>
                    <span>{t.bookPrice}</span>
                    <span style={{ textAlign: 'right' }}>{t.bookQty}</span>
                    <span style={{ textAlign: 'right' }}>t</span>
                  </div>
                  {tapeRows.length ? tapeRows : (
                    <div style={{ color: 'var(--muted)', fontSize: 11 }}>{t.loadTape}</div>
                  )}
                </div>
              </div>
            );
          })() : <div style={{ color: 'var(--muted)' }}>{t.loadBook}</div>
        )}

        {tab === 'positions' && (
          hasKey ? (
            shownPositions.length ? (
              <>
                <table style={table}>
                  <colgroup>
                    <col style={{ width: '16%' }} />
                    <col style={{ width: '9%' }} />
                    <col style={{ width: '10%' }} />
                    <col style={{ width: '14%' }} />
                    <col style={{ width: '16%' }} />
                    <col style={{ width: '16%' }} />
                    <col style={{ width: '19%' }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th style={{ ...th, textAlign: 'left' }}>Symbol</th>
                      <th style={{ ...th, textAlign: 'left' }}>Side</th>
                      <th style={{ ...th, textAlign: 'left' }} title={t.posChipTitle}>{t.posChipCol}</th>
                      <th style={{ ...th, textAlign: 'right' }} title="Coin qty">Qty</th>
                      <th style={{ ...th, textAlign: 'right' }} title="Notional USDT">Size USDT</th>
                      <th style={{ ...th, textAlign: 'right' }}>Entry</th>
                      <th style={{ ...th, textAlign: 'right' }}>uPnL</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shownPositions.map((p, i) => {
                      const side: 'long' | 'short' = String(p.side || '').toLowerCase().includes('short')
                        || Number(p.positionAmt) < 0
                        ? 'short'
                        : 'long';
                      const key = positionKey({ symbol: p.symbol, side });
                      const selected = selectedPositionKey === key;
                      const short = side === 'short';
                      const qty = Math.abs(Number(p.size ?? p.positionAmt ?? 0));
                      const mark = Number(p.markPrice);
                      const entry = Number(p.entryPrice);
                      const px = Number.isFinite(mark) && mark > 0
                        ? mark
                        : (Number.isFinite(entry) && entry > 0 ? entry : 0);
                      const usdt = qty > 0 && px > 0 ? qty * px : NaN;
                      const sym = normSym(p.symbol) || '—';
                      const chip = sym !== '—' ? (chipBySymbol[sym] || '') : '';
                      return (
                        <tr
                          key={key || i}
                          onClick={() => onSelectPosition?.(selected ? null : key, selected ? null : p)}
                          style={{
                            cursor: onSelectPosition ? 'pointer' : undefined,
                            background: selected
                              ? (short ? 'rgba(226,91,91,0.08)' : 'rgba(61,186,122,0.08)')
                              : undefined,
                            boxShadow: selected
                              ? `inset 2px 0 0 ${short ? 'var(--short)' : 'var(--long)'}`
                              : undefined,
                          }}
                          title={onSelectPosition ? t.posSelectHint : undefined}
                        >
                          <td style={{ ...td, textAlign: 'left' }}>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (onJumpToSymbol && sym !== '—') onJumpToSymbol(sym);
                                else onSelectPosition?.(selected ? null : key, selected ? null : p);
                              }}
                              style={symLink}
                              title={t.posJumpTab}
                            >
                              {sym}
                            </button>
                          </td>
                          <td style={{ ...td, textAlign: 'left', color: short ? 'var(--short)' : 'var(--long)' }}>
                            {short ? 'short' : 'long'}
                          </td>
                          <td style={{ ...td, textAlign: 'left' }} title={chip ? t.posChipTitle : undefined}>
                            {chip ? (
                              <span style={{
                                fontSize: 10, fontWeight: 700, fontFamily: 'var(--mono)',
                                color: 'var(--long)', letterSpacing: 0.02,
                              }}>
                                {chip}
                              </span>
                            ) : null}
                          </td>
                          <td style={{ ...td, textAlign: 'right' }} title={fmt(qty, 4)}>{fmt(qty, 4)}</td>
                          <td style={{ ...td, textAlign: 'right', color: 'var(--gold)' }} title={Number.isFinite(usdt) ? `${fmt(usdt, 2)} USDT` : undefined}>
                            {Number.isFinite(usdt) ? fmt(usdt, 2) : '—'}
                          </td>
                          <td style={{ ...td, textAlign: 'right' }}>{fmt(p.entryPrice)}</td>
                          <td style={{
                            ...td,
                            textAlign: 'right',
                            color: Number(p.unrealizedPnl) >= 0 ? 'var(--long)' : 'var(--short)',
                          }}>
                            {fmt(p.unrealizedPnl, 2)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {positionTools ? (
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 4 }}>
                    {positionTools}
                  </div>
                ) : null}
              </>
            ) : (
              <div style={{ color: 'var(--muted)' }}>
                {groupByActiveTab && positions.length
                  ? t.posGroupByTabEmpty
                  : t.noPositions}
              </div>
            )
          ) : <div style={{ color: 'var(--muted)' }}>{t.connectKeyPositions}</div>
        )}

        {tab === 'orders' && (
          hasKey ? (
            shownOrders.length ? (
              <table style={table}>
                <colgroup>
                  <col style={{ width: '22%' }} />
                  <col style={{ width: '14%' }} />
                  <col style={{ width: '16%' }} />
                  <col style={{ width: '24%' }} />
                  <col style={{ width: '24%' }} />
                </colgroup>
                <thead>
                  <tr>
                    <th style={{ ...th, textAlign: 'left' }}>Symbol</th>
                    <th style={{ ...th, textAlign: 'left' }}>Side</th>
                    <th style={{ ...th, textAlign: 'left' }}>Type</th>
                    <th style={{ ...th, textAlign: 'right' }}>Qty</th>
                    <th style={{ ...th, textAlign: 'right' }}>Price</th>
                  </tr>
                </thead>
                <tbody>
                  {shownOrders.map((o, i) => (
                    <tr key={o.orderId || i}>
                      <td style={{ ...td, textAlign: 'left' }}>{o.symbol || '—'}</td>
                      <td style={{ ...td, textAlign: 'left' }}>{o.side || '—'}</td>
                      <td style={{ ...td, textAlign: 'left' }}>{o.type || '—'}</td>
                      <td style={{ ...td, textAlign: 'right' }}>{fmt(o.origQty ?? o.quantity, 4)}</td>
                      <td style={{ ...td, textAlign: 'right' }}>{fmt(o.price)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div style={{ color: 'var(--muted)' }}>
                {groupByActiveTab && orders.length ? t.ordersGroupByTabEmpty : t.noOrders}
              </div>
            )
          ) : <div style={{ color: 'var(--muted)' }}>{t.connectKeyOrders}</div>
        )}

        {tab === 'trades' && (
          hasKey ? (
            shownTrades.length ? (
              <table style={{ ...table, fontSize: 12 }}>
                <colgroup>
                  <col style={{ width: '7%' }} />
                  <col style={{ width: '16%' }} />
                  <col style={{ width: '22%' }} />
                  <col style={{ width: '14%' }} />
                  <col style={{ width: '12%' }} />
                  <col style={{ width: '14%' }} />
                  <col style={{ width: '15%' }} />
                </colgroup>
                <thead>
                  <tr>
                    <th style={th} />
                    <th style={{ ...th, textAlign: 'left' }}>Symbol</th>
                    <th style={{ ...th, textAlign: 'left' }}>Side</th>
                    <th style={{ ...th, textAlign: 'right' }}>Price</th>
                    <th style={{ ...th, textAlign: 'right' }}>Qty</th>
                    <th style={{ ...th, textAlign: 'right' }} title="Realized PnL USDT">PnL</th>
                    <th style={{ ...th, textAlign: 'right' }}>Time</th>
                  </tr>
                </thead>
                <tbody>
                  {shownTrades.map((row, i) => {
                    const pnl = row.realizedPnl;
                    const pct = row.realizedPnlPct;
                    const hasPnl = pnl != null && Number.isFinite(pnl);
                    const hasPct = pct != null && Number.isFinite(pct);
                    const pnlTxt = !hasPnl
                      ? (row.reduceOnly ? '—' : '')
                      : `${pnl! >= 0 ? '+' : ''}${fmt(pnl, 4)}`;
                    const pctTxt = hasPct
                      ? `${pct! >= 0 ? '+' : ''}${fmt(pct, 3)}%`
                      : '';
                    return (
                      <tr key={i}>
                        <td style={{ ...td, textAlign: 'left' }}>
                          {row.real ? (
                            <span
                              title={row.label || 'Real WEEX fill'}
                              style={{
                                color: '#1a1408',
                                background: '#f5d76e',
                                fontSize: 10,
                                fontWeight: 700,
                                letterSpacing: 0.04,
                                padding: '1px 5px',
                                borderRadius: 3,
                              }}
                            >
                              REAL
                            </span>
                          ) : null}
                        </td>
                        <td style={{ ...td, textAlign: 'left' }}>{row.symbol || '—'}</td>
                        <td style={{ ...td, textAlign: 'left' }}>
                          {row.label || row.side || (row.isBuyerMaker ? 'sell' : 'buy')}
                          {row.reduceOnly ? ' · exit' : ''}
                        </td>
                        <td style={{ ...td, textAlign: 'right' }}>{fmt(row.price)}</td>
                        <td style={{ ...td, textAlign: 'right' }}>{fmt(row.qty ?? row.quantity, 4)}</td>
                        <td
                          title={hasPnl ? 'Realized USDT · % of position move (PnL / entry notional)' : undefined}
                          style={{
                            ...td,
                            textAlign: 'right',
                            color: !hasPnl
                              ? 'var(--muted)'
                              : pnl! >= 0 ? 'var(--long)' : 'var(--short)',
                            whiteSpace: 'nowrap',
                            lineHeight: 1.25,
                          }}
                        >
                          {pnlTxt}
                          {pctTxt ? (
                            <div style={{ fontSize: 10, opacity: 0.9 }}>{pctTxt}</div>
                          ) : null}
                        </td>
                        <td style={{ ...td, textAlign: 'right', fontSize: 11 }}>
                          {row.time ? new Date(row.time).toLocaleString(lang === 'ru' ? 'ru-RU' : 'en-US') : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <div style={{ color: 'var(--muted)' }}>
                {groupByActiveTab && trades.length ? t.tradesGroupByTabEmpty : t.noClosedTrades}
              </div>
            )
          ) : <div style={{ color: 'var(--muted)' }}>{t.connectKeyTrades}</div>
        )}

        {tab === 'balance' && (
          hasKey && balance ? (
            <div>
              <div>Equity: <b style={{ color: 'var(--gold)' }}>{fmt(balance.equity, 2)}</b> USDT</div>
              <div>Available: {fmt(balance.available, 2)} USDT</div>
              {marginLabel ? <div style={{ marginTop: 8, color: 'var(--muted)' }}>{marginLabel}</div> : null}
            </div>
          ) : (
            <div style={{ color: 'var(--muted)' }}>
              {!hasKey ? t.connectKeyPositions : t.noBalance}
              {hasKey ? (
                <div style={{ marginTop: 8 }}>
                  <button type="button" onClick={refresh} style={tabBtn}>↻ {t.refresh}</button>
                </div>
              ) : null}
            </div>
          )
        )}
      </div>
    </div>
  );
}

const tabBtn: CSSProperties = { padding: '6px 10px', borderRadius: 8, border: '1px solid var(--line)', background: 'var(--panel-2)', color: 'var(--text)', cursor: 'pointer' };
const table: CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  tableLayout: 'fixed',
};
const th: CSSProperties = {
  padding: '5px 8px',
  borderBottom: '1px solid rgba(232,237,245,0.14)',
  color: 'var(--muted)',
  fontWeight: 600,
  fontSize: 11,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};
const td: CSSProperties = {
  padding: '5px 8px',
  borderBottom: '1px solid rgba(232,237,245,0.07)',
  borderRight: '1px solid rgba(232,237,245,0.05)',
  verticalAlign: 'middle',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};
const symLink: CSSProperties = {
  background: 'transparent',
  border: 'none',
  padding: 0,
  margin: 0,
  color: 'var(--text)',
  fontFamily: 'var(--mono)',
  fontSize: 12,
  cursor: 'pointer',
  textDecoration: 'underline',
  textDecorationColor: 'rgba(201,162,39,0.45)',
  textUnderlineOffset: 2,
};
